import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getRandomTopicImage } from '../../../utils/randomTopicImage';

const BLUE = '#47ABDB';

// Same headline treatment as the page's own hero (bold caps + italic
// subtitle, no background box) rather than a separate colored banner.
const SectionHeadline = styled.div`color:${BLUE};font-weight:800;text-transform:uppercase;letter-spacing:.04em;font-size:1.35rem;margin-bottom:.2rem;`;
const SectionSubtitle = styled.div`color:#5B5B5B;font-style:italic;font-size:.92rem;margin-bottom:1.1rem;`;

const Row = styled.section`& + &{margin-top:1.3rem;}`;
const GroupLabel = styled.div`color:#003C72;font-weight:800;text-transform:uppercase;letter-spacing:.04em;font-size:.8rem;margin-bottom:.55rem;`;
// Two cards per row while each can be at least 17rem wide, else one: decided by the width the section
// actually gets (a narrow profile column at 900px squeezed two cards into strips), not by the screen.
const Grid = styled.div`display:grid;gap:1rem;grid-template-columns:repeat(auto-fit,minmax(max(17rem,calc((100% - 1rem) / 2)),1fr));`;
const Card = styled.div`background:#fff;box-shadow:0 6px 16px rgba(15,23,42,.06);padding:1.1rem 1.2rem 1rem;position:relative;display:flex;flex-direction:column;overflow:hidden;`;
const NewRibbon = styled.div`
  position:absolute;top:0;right:0;background:linear-gradient(to right,${BLUE},#A0C9DA);color:#fff;
  font-size:.62rem;font-weight:800;letter-spacing:.03em;padding:.2rem 2.2rem;transform:rotate(45deg) translate(1.6rem,-.2rem);
`;
const TopRow = styled.div`display:flex;align-items:flex-start;justify-content:space-between;gap:.75rem;`;
const Tag = styled.span`display:inline-block;background:#003C72;color:#fff;font-size:.68rem;font-weight:600;padding:.2rem .55rem;align-self:flex-start;`;
const TopicImage = styled.img`width:56px;height:56px;object-fit:cover;border-radius:6px;flex:none;`;
const Title = styled.div`color:${BLUE};font-weight:800;font-size:1.18rem;line-height:1.32;margin-top:.65rem;`;
// Three lines at most; the card's free height goes to Spacer below it, not to the excerpt (a taller
// box would show the start of a fourth line under the clamp).
const Excerpt = styled.p`
  color:#0f2a3d;font-size:.82rem;line-height:1.5;margin:.5rem 0 0;max-height:4.5em;
  display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;
`;
const Spacer = styled.div`flex:1;`;
const SummaryLanguage = styled.div`color:#5B6B78;font-size:.7rem;margin-top:.35rem;`;
const MatchNote = styled.div`color:#0f4c68;font-size:.72rem;font-style:italic;margin-top:.5rem;`;
const ReadButton = styled.a`
  display:flex;align-items:center;justify-content:center;margin-top:.85rem;padding:.6rem 1rem;
  color:#fff;font-weight:700;font-size:.85rem;text-decoration:none;
  background:linear-gradient(to right,${BLUE},#A0C9DA);transition:opacity .2s;
  &:hover{opacity:.9;}
`;

// The studies fetch can take a while - rather than the section just staying
// blank, show an animated ECG trace (an actual heart monitor line, fitting
// for a cardiology-research feed) until it resolves. Same seamless-marquee
// technique as PartnerSection's logo slider: two identical copies of the
// trace sit side by side and slide left by exactly one copy's width, so the
// loop never visibly resets.
const ecgScroll = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
`;
const EcgWrap = styled.div`
  position:relative;overflow:hidden;height:64px;border-radius:8px;background:#eef7fb;
  &::after{content:'';position:absolute;inset:0;left:auto;width:2.5rem;background:linear-gradient(to right,transparent,#eef7fb);}
`;
const EcgTrack = styled.div`
  display:flex;width:fit-content;height:100%;align-items:center;
  animation:${ecgScroll} 2.4s linear infinite;
`;
const EcgSvg = styled.svg`
  display:block;flex:none;filter:drop-shadow(0 0 3px rgba(71,171,219,.55));
`;
const LoadingCaption = styled.p`color:#5B6B78;font-size:.82rem;font-style:italic;margin:.6rem 0 0;`;

// One heartbeat "blip" (flat - QRS spike - flat), 70 units wide so several
// can be chained edge to edge into a continuous trace.
const BEAT = [[0, 20], [26, 20], [30, 5], [34, 35], [38, 12], [42, 20], [70, 20]];
const BEATS_PER_COPY = 6;
const ecgPath = Array.from({ length: BEATS_PER_COPY }, (_, i) => i)
  .flatMap((i) => BEAT.filter((_, idx) => i === 0 || idx > 0).map(([x, y]) => [x + i * 70, y]))
  .map(([x, y], idx) => `${idx === 0 ? 'M' : 'L'}${x},${y}`)
  .join(' ');

const EcgTrace = () => (
  <EcgSvg width="280" height="48" viewBox={`0 0 ${BEATS_PER_COPY * 70} 40`} preserveAspectRatio="none">
    <path d={ecgPath} fill="none" stroke={BLUE} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </EcgSvg>
);

// The three rows of the HEARTPORT EXPERT section (GET /api/me/studies): Austrian research, studies
// from top journals and new studies, two each, chosen on the backend (studies.StudyFeed) by the
// person's interests, with general cardiology filling what the interests did not. `groups` is
// null while the answer is pending: the heading shows with the ECG trace. A row Expert had nothing
// for is left out, and the whole section when all three are empty.
const GROUPS = [
  { key: 'austrian', id: 'profile.studies.group.austrian', fallback: 'Austrian research' },
  { key: 'top', id: 'profile.studies.group.top', fallback: 'From top journals' },
  { key: 'new', id: 'profile.studies.group.new', fallback: 'New' },
];

// subjectName: on a public profile (publicProfile/PublicProfilePage), the person's short name - the
// notes then say why the study is on their page ("Co-authored by …", "… is interested in …").
const RecommendedStudies = ({ t, groups, subjectName }) => {
  const { language } = useLanguage();

  // Picked once per answer (not on every render) - same random-image-per-card idea as Heartport
  // Expert's own PubMedCard/ArticleCard, just not re-rolled on every unrelated re-render.
  const imagesByPmid = useMemo(() => {
    const map = {};
    GROUPS.forEach(({ key }) => (groups?.[key] || []).forEach((study) => { map[study.pmid] = getRandomTopicImage(); }));
    return map;
  }, [groups]);

  const matchNote = (study) => {
    if (study.reason === 'COAUTHOR' && subjectName) {
      return t('profile.studies.coauthored', 'Co-authored by {name}', { name: subjectName });
    }
    if (!study.matchedInterest) return null;
    const interest = t(`speaker.interest.${study.matchedInterest}`, study.matchedInterest);
    return subjectName
      ? t('profile.studies.subjectInterested', '{name} is interested in {interest}', { name: subjectName, interest })
      : t('profile.studies.matchedInterest', 'Because you’re interested in {interest}', { interest });
  };

  const rows = GROUPS.filter(({ key }) => (groups?.[key] || []).length > 0);
  if (groups && rows.length === 0) return null;

  return (
    <div>
      {/* Brand name + tagline, hardcoded like the profile's own hero
          headline - not routed through react-intl. */}
      <SectionHeadline>HEARTPORT EXPERT</SectionHeadline>
      <SectionSubtitle>Latest Research for busy Doctors</SectionSubtitle>
      {!groups ? (
        <>
          <EcgWrap>
            <EcgTrack>
              <EcgTrace />
              <EcgTrace />
            </EcgTrack>
          </EcgWrap>
          <LoadingCaption>{t('profile.studies.loading', 'Reading the latest research…')}</LoadingCaption>
        </>
      ) : rows.map(({ key, id, fallback }) => (
        <Row key={key} aria-label={t(id, fallback)}>
          <GroupLabel>{t(id, fallback)}</GroupLabel>
          <Grid>
            {groups[key].map((study) => {
              const image = imagesByPmid[study.pmid];
              return (
                <Card key={study.pmid}>
                  {study.isNew && <NewRibbon>{t('profile.studies.new', 'NEW')}</NewRibbon>}
                  <TopRow>
                    {study.journalName && <Tag>{study.journalName}</Tag>}
                    {image && <TopicImage src={image} alt="" />}
                  </TopRow>
                  <Title>{study.title}</Title>
                  {study.excerpt && <Excerpt lang={study.textLanguage}>{study.excerpt}</Excerpt>}
                  <Spacer />
                  {/* An English page shows the German summary when Expert has no English one. */}
                  {study.textLanguage && study.textLanguage !== language && (
                    <SummaryLanguage>{t('profile.studies.germanSummary', 'German summary')}</SummaryLanguage>
                  )}
                  {matchNote(study) && <MatchNote>{matchNote(study)}</MatchNote>}
                  <ReadButton href={study.url} target="_blank" rel="noreferrer">{t('profile.studies.readStudy', 'Read study')}</ReadButton>
                </Card>
              );
            })}
          </Grid>
        </Row>
      ))}
    </div>
  );
};

export default RecommendedStudies;
